import { ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { baseUrl } from '@/api'
import Pusher from 'pusher-js'
import { Device } from '@twilio/voice-sdk'
import { useUserStore } from '@/stores/user'
import { useDeviceStore } from './device'
import { useRegisteredStore } from './registered'
import { useStatusStore } from './status'
import { useSmsStore } from './sms'

export const usePusherStore = defineStore('pusher', () => {
  const pusher = ref<Pusher | null>(null)
  const userStore = useUserStore()

  const registeredStore = useRegisteredStore()
  const { registered } = storeToRefs(registeredStore)

  const deviceStore = useDeviceStore()
  const { device } = storeToRefs(deviceStore)

  const statusStore = useStatusStore()
  const { status } = storeToRefs(statusStore)

  // Track channel subscription for cleanup
  let userChannel: ReturnType<Pusher['subscribe']> | null = null

  const disconnect = () => {
    if (userChannel) {
      userChannel.unbind_all()
      pusher.value?.unsubscribe(userChannel.name)
      userChannel = null
    }
    if (pusher.value) {
      pusher.value.disconnect()
      pusher.value = null
    }
  }

  const init = () => {
    // Clean up existing subscriptions before reinitializing
    disconnect()
    // @ts-expect-error Pusher has some custom inteface Ajax
    Pusher.Runtime.createXHR = () => {
      const xhr = new XMLHttpRequest()
      xhr.withCredentials = true
      return xhr
    }

    pusher.value = new Pusher(import.meta.env.VITE_PUSHER_APP_KEY, {
      authEndpoint: baseUrl + '/api/v1/pusher/auth',
      cluster: 'us2',
      forceTLS: true,
    })

    userChannel = pusher.value.subscribe(`user-${userStore.user?.id}`)

    // The server sends this to put a wedged device back in service — see
    // CallService::failStuckAppCall. Whether to act on it is decided by the
    // device's own state, not the cached `registered` flag: the device 'error'
    // handler deliberately leaves that flag true through the recoverable
    // signaling errors (31000/31005/31009), and a device stuck in 'registering'
    // behind those is exactly the wedge this event is sent to clear. Gating the
    // reload on the flag meant the nudge arrived and did nothing in the one
    // case it was written for. Both are reported to the server, which only logs
    // them, so a disagreement between the two is visible in that telemetry.
    userChannel.bind('refresh', () => {
      console.log('Pusher refresh event received...')

      const twilioDevice = device.value
      const isDeviceRegistered = twilioDevice?.state === Device.State.Registered

      const data = new Blob(
        [
          JSON.stringify({
            account_id: userStore.user?.account_id,
            user_id: userStore.user?.id,
            registered: registered.value,
            device_state: twilioDevice?.state ?? null,
          }),
        ],
        { type: 'application/json' },
      )
      navigator.sendBeacon(`${baseUrl}/api/v1/user/refresh`, data)

      // Reload only when the agent is idle at the waiting screen, the same bar
      // the signaling watchdog holds itself to (see armSignalingWatchdog) and
      // for the same reasons. A live call, because signaling can drop mid-call
      // and leave the device short of 'registered' while the call is still up,
      // and this event goes to the agent's user channel for any of their calls
      // — so a reload could hang up on whoever they are talking to. And
      // anything after one: isBusy goes false the moment a call closes, while
      // the agent may still be part-way through a disposition or a note that a
      // reload would throw away.
      //
      // Not covered, deliberately: the second or two inside makeOutboundCall
      // where status is still 'waiting' and no call exists yet. That state
      // lives in useCall, which cannot be imported here without a cycle
      // (stores/user already imports this store), and a reload there costs a
      // dial the server has just told us was never going to connect anyway.
      if (twilioDevice?.isBusy || status.value !== 'waiting') {
        console.log(`Agent is mid-workflow ("${status.value}"). Not reloading.`)

        return
      }

      if (!isDeviceRegistered) {
        console.log(`Device is "${twilioDevice?.state ?? 'missing'}". Refreshing...`)
        window.location.reload()
      } else {
        console.log('Device registered. Not taking any action.')
      }
    })

    // SMS events (filtered by conversation permissions)
    const smsStore = useSmsStore()

    userChannel.bind('sms-received', (data: unknown) => {
      smsStore.handleSmsReceived(data as Parameters<typeof smsStore.handleSmsReceived>[0])
    })

    userChannel.bind('sms-sent', (data: unknown) => {
      smsStore.handleSmsSent(data as Parameters<typeof smsStore.handleSmsSent>[0])
    })

    userChannel.bind('sms-delivery-status', (data: unknown) => {
      smsStore.handleSmsDeliveryStatus(
        data as Parameters<typeof smsStore.handleSmsDeliveryStatus>[0],
      )
    })

    userChannel.bind('sms-unread-updated', (data: unknown) => {
      smsStore.handleUnreadUpdated(data as Parameters<typeof smsStore.handleUnreadUpdated>[0])
    })

    // const userPresence = pusher.value.subscribe(`presence-app-${userStore.user?.id}`)

    // userPresence.bind('pusher:subscription_succeeded', () => {
    //   console.log('Pusher live')
    // })

    // userPresence.bind('pusher:subscription_error', () => {
    //   console.log('Pusher error')
    // })
  }

  return { pusher, init, disconnect }
})
